import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult
} from '@tanstack/react-query';

import { type Entry } from '@/types/cit-public-api';

export interface PaginatedEntries {
  entries: Entry[];
  total: number;
  cursor: string | undefined;
}

export function lookupEntry(pages: PaginatedEntries[] | undefined, entryId: string) {
  if (!pages) {
    return undefined;
  }

  for (const page of pages) {
    const foundEntry = page.entries.find((entry) => entry.slug === entryId || entry.id === entryId);
    if (foundEntry) {
      return foundEntry;
    }
  }
}

export async function getPrevOrNextEntry(
  pages: PaginatedEntries[] | undefined,
  entryId: string,
  direction: 'prev' | 'next',
  fetchNextPage?: (
    options?: FetchNextPageOptions | undefined
  ) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<{ entries: Entry[]; total: number; cursor: string | undefined }, unknown>,
      Error
    >
  >
): Promise<Entry | undefined> {
  if (pages) {
    for (const [pageIdx, page] of pages.entries()) {
      const idx = page.entries.findIndex((entry) => entry.slug === entryId || entry.id === entryId);
      if (idx > -1) {
        if (direction === 'prev') {
          const prevEntryId =
            idx === 0
              ? pageIdx === 0
                ? undefined
                : pages[pageIdx - 1].entries[pages[pageIdx - 1].entries.length - 1]
              : page.entries[idx - 1];

          return prevEntryId;
        } else {
          let nextEntryId =
            idx === page.entries.length - 1
              ? pageIdx === pages.length - 1
                ? undefined
                : pages[pageIdx + 1].entries[0]
              : page.entries[idx + 1];

          if (!nextEntryId && fetchNextPage) {
            const result = await fetchNextPage();
            const updatedPages = result.data?.pages;
            nextEntryId = updatedPages?.[pageIdx + 1]?.entries[0];
          }

          return nextEntryId;
        }
      }
    }
  }

  return undefined;
}

/**
 * Gets an array window/slice of entries from the provided pages centered around a specific entry as well as the previous and next entry ids without flat mapping all of the pages
 * @param pages - The pages of entries from the react-query data
 * @param entryId - The id of the entry to center the window around
 * @param windowSize - The number of entries on each side of the target entry to include in the window
 * @param fetchNextPage - The function to fetch the next page, is undefined if next page is not available
 */
export function getEntryWindow(
  pages: PaginatedEntries[] | undefined,
  entryId: string,
  windowSize: number,
  fetchNextPage?: (
    options?: FetchNextPageOptions | undefined
  ) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<{ entries: Entry[]; total: number; cursor: string | undefined }, unknown>,
      Error
    >
  >
): (Entry | undefined)[] {
  if (!pages) {
    return [];
  }

  const entryWindow: (Entry | undefined)[] = [];

  // First find the target entry and its position
  let targetPageIdx = -1;
  let targetIdx = -1;

  for (const [pageIdx, page] of pages.entries()) {
    const idx = page.entries.findIndex((entry) => entry.slug === entryId || entry.id === entryId);
    if (idx > -1) {
      targetPageIdx = pageIdx;
      targetIdx = idx;
      // Add the target entry to the result
      entryWindow.push(page.entries[idx]);
      break;
    }
  }

  // If the target entry is not found, return an empty array
  if (targetPageIdx === -1 || targetIdx === -1) {
    return [];
  }

  // Add entries before the target
  let entriesBefore = 0;
  let currentPageIdx = targetPageIdx;
  let currentIdx = targetIdx - 1;

  while (entriesBefore < windowSize) {
    if (currentIdx >= 0) {
      const entry = pages[currentPageIdx].entries[currentIdx];
      if (entry) {
        entryWindow.unshift(entry);
        entriesBefore++;
      }
      currentIdx--;
    } else if (currentPageIdx > 0) {
      currentPageIdx--;
      currentIdx = pages[currentPageIdx].entries.length - 1;
    } else {
      entryWindow.unshift(undefined);
      entriesBefore++;
    }
  }

  // Add entries after the target
  let entriesAfter = 0;
  currentPageIdx = targetPageIdx;
  currentIdx = targetIdx + 1;

  while (entriesAfter < windowSize) {
    if (currentIdx < pages[currentPageIdx].entries.length) {
      const entry = pages[currentPageIdx].entries[currentIdx];
      if (entry) {
        entryWindow.push(entry);
        entriesAfter++;
      }
      currentIdx++;
    } else if (currentPageIdx < pages.length - 1) {
      currentPageIdx++;
      currentIdx = 0;
    } else {
      // If we're at the end of the last page and we need more entries, fetch the next page
      if (fetchNextPage && entriesAfter < windowSize) {
        try {
          fetchNextPage();
        } catch (error) {
          console.error('Error fetching next page', error);
        }
      } else {
        entryWindow.push(undefined);
        entriesAfter++;
      }
    }
  }

  return entryWindow;
}

// Helper function with proper typing
export function getEntryLink(
  accountId: string,
  entryId: string,
  classificationId?: string,
  folderId?: string,
  fromAccountSearch?: boolean,
  fromAllSearch?: boolean,
  prefix?: string
) {
  if (classificationId) {
    return `${
      prefix ? `/${prefix}` : ''
    }/classifications/${classificationId}/${accountId}/entry/${entryId}`;
  } else if (fromAllSearch) {
    return `${prefix ? `/${prefix}` : ''}/search/${accountId}/entry/${entryId}`;
  } else if (fromAccountSearch) {
    return `${prefix ? `/${prefix}` : ''}/${accountId}/entry/${entryId}`;
  } else if (folderId === 'all' || !folderId) {
    return `${prefix ? `/${prefix}` : ''}/${accountId}/folder/entry/${entryId}`;
  } else {
    return `${prefix ? `/${prefix}` : ''}/${accountId}/folder/${folderId}/entry/${entryId}`;
  }
}

export function getFolderLink(accountId: string, folderId?: string, prefix?: string) {
  // passing no folderId will get all entries for the account
  return `${prefix ? `/${prefix}` : ''}/${accountId}/folder${folderId ? `/${folderId}` : ''}`;
}

export function getClassificationLink(classificationId: string, encode = false) {
  return `/classifications/${encode ? encodeURIComponent(classificationId) : classificationId}`;
}
